import { Amplify } from 'aws-amplify';
import { signIn, signUp, confirmSignUp, signOut, getCurrentUser, fetchAuthSession, resendSignUpCode, resetPassword, confirmResetPassword, fetchUserAttributes } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { AUTH_CONFIG, API_CONFIG, validateAuthConfig, isOAuthEnabled } from '../config/auth';

// Initialize Amplify with Cognito configuration
const initializeAuth = () => {
  try {
    if (validateAuthConfig()) {
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('Configuring AWS Amplify for region:', process.env.REACT_APP_AWS_REGION);
      }
      
      Amplify.configure(AUTH_CONFIG);
    } else {
      console.warn('Running in development mode without AWS Cognito');
    }
  } catch (error) {
    console.error('Failed to configure AWS Amplify:', error.message);
  }
};

// Authentication service class
class AuthService {
  constructor() {
    this.currentUser = null;
    this.token = null;
    this.initializeListeners();
  }

  // Format error messages to be more user-friendly
  formatErrorMessage(error) {
    console.log('🔍 formatErrorMessage called with:', error);
    
    // Handle different error formats
    let message = '';
    let errorType = '';
    
    if (typeof error === 'string') {
      message = error;
    } else if (error && typeof error === 'object') {
      // Handle AWS error format like {"__type":"NotAuthorizedException","message":"Incorrect username or password."}
      message = error.message || error.toString();
      errorType = error.__type || error.code || error.name || '';
    } else {
      message = String(error);
    }
    
    // Debug logging for error formatting (verbose mode only)
    if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
      console.log('Error formatting debug:', { 
        originalError: error, 
        message, 
        errorType
      });
    }
    
    // Common AWS Cognito error mappings
    const errorMappings = {
      'Cannot reset password for the user as there is no registered/verified email or phone_number': 
        'No account found with this email address. Please check your email or sign up for a new account.',
      'User does not exist': 
        'No account found with this email address. Please check your email or sign up for a new account.',
      'Incorrect username or password': 
        'Invalid email or password. Please check your credentials and try again.',
      'User is not confirmed': 
        'Please verify your email address before signing in. Check your inbox for a confirmation code.',
      'Invalid verification code provided': 
        'The verification code is incorrect. Please check the code and try again.',
      'User already exists': 
        'An account with this email already exists. Please sign in instead.',
      'Password does not conform to policy': 
        'Password must be at least 8 characters and contain uppercase, lowercase, and number.',
      'Username/client id combination not found': 
        'Invalid email or password. Please check your credentials and try again.',
      'NotAuthorizedException': 
        'Invalid email or password. Please check your credentials and try again.',
      'UserNotFoundException': 
        'No account found with this email address. Please check your email or sign up for a new account.',
      'UsernameExistsException': 
        'An account with this email already exists. Please sign in instead.',
      'CodeMismatchException': 
        'The verification code is incorrect. Please check the code and try again.',
      'ExpiredCodeException': 
        'The verification code has expired. Please request a new code.',
      'LimitExceededException': 
        'Too many attempts. Please wait a moment before trying again.',
      'TooManyRequestsException': 
        'Too many requests. Please wait a moment before trying again.',
      'AliasExistsException': 
        'An account with this email already exists. Please sign in instead.',
      'InvalidPasswordException': 
        'Password does not meet requirements. Please use at least 8 characters with uppercase, lowercase, and numbers.',
      'InvalidParameterException': 
        'Invalid input provided. Please check your information and try again.'
    };    // Check for exact matches first (message)
    if (errorMappings[message]) {
      return errorMappings[message];
    }

    // Check for exact matches (error type)
    if (errorMappings[errorType]) {
      return errorMappings[errorType];
    }

    // Check for partial matches (message)
    for (const [key, value] of Object.entries(errorMappings)) {
      if (message.includes(key)) {
        return value;
      }
    }

    // Check for partial matches (error type)
    for (const [key, value] of Object.entries(errorMappings)) {
      if (errorType.includes(key)) {
        return value;
      }
    }

    // Default fallback
    if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
      console.log('No error mapping found, using original message:', message);
    }
    return message || 'An error occurred. Please try again.';
  }

  // Initialize authentication event listeners
  initializeListeners() {
    // Listen for authentication state changes
    Hub.listen('auth', (data) => {
      const { payload } = data;
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('Auth event:', payload.event);
      }
      
      switch (payload.event) {
        case 'signInWithRedirect':
        case 'signedIn':
          this.handleSignIn(payload.data);
          break;
        case 'signedOut':
          this.handleSignOut();
          break;
        case 'signInWithRedirect_failure':
        case 'signIn_failure':
          this.handleSignInFailure(payload.data);
          break;
        case 'tokenRefresh':
          this.handleTokenRefresh(payload.data);
          break;
        case 'tokenRefresh_failure':
          this.handleTokenRefreshFailure(payload.data);
          break;
        default:
          break;
      }
    });
  }

  // Get current authenticated user
  async getCurrentUser() {
    try {
      const user = await getCurrentUser();
      this.currentUser = user;
      
      // Get and store JWT token - force refresh to ensure we get latest
      const session = await fetchAuthSession({ forceRefresh: true });
      if (session?.tokens?.idToken) {
        this.token = session.tokens.idToken.toString();
        localStorage.setItem(API_CONFIG.jwt.storageKey, this.token);
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('✅ JWT token stored in getCurrentUser');
        }
      } else {
        console.warn('⚠️ No ID token available in session');
      }
      
      return user;
    } catch (error) {
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('🔍 getCurrentUser failed, checking SAML authentication...', error.message);
      }
      
      // Check if user is authenticated via SAML
      const samlAuth = await this.checkSAMLAuthentication();
      if (samlAuth.success) {
        this.currentUser = samlAuth.user;
        
        // Use ID token from SAML for API calls
        if (samlAuth.tokens && samlAuth.tokens.id_token) {
          this.token = samlAuth.tokens.id_token;
          localStorage.setItem(API_CONFIG.jwt.storageKey, this.token);
          if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
            console.log('✅ JWT token stored from SAML authentication');
          }
        }
        
        return samlAuth.user;
      }
      
      this.currentUser = null;
      this.token = null;
      localStorage.removeItem(API_CONFIG.jwt.storageKey);
      return null;
    }
  }

  // Sign in with username/email and password
  async signIn(username, password) {
    try {
      // Check if authentication is properly configured
      if (!validateAuthConfig()) {
        return { success: false, error: 'Authentication is not configured. Please check your environment variables.' };
      }

      const result = await signIn({ username, password });
      
      // Handle MFA or other challenges if needed
      if (!result.isSignedIn) {
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('Authentication challenge required:', result.nextStep);
        }
        return { success: false, challenge: result.nextStep, user: result };
      }
      
      await this.getCurrentUser(); // Update current user and token
      return { success: true, user: this.currentUser };
    } catch (error) {
      console.error('❌ AuthService.signIn failed - RAW ERROR:', error);
      console.error('🔍 Error details:', {
        type: typeof error,
        constructor: error.constructor?.name,
        keys: error ? Object.keys(error) : 'N/A',
        __type: error?.__type,
        code: error?.code,
        name: error?.name,
        message: error?.message
      });
      
      // Handle specific AWS Amplify configuration errors
      if (error?.message && error.message.includes('Auth UserPool not configured')) {
        const configError = 'AWS Cognito is not properly configured. Please check your environment variables and restart the application.';
        return { success: false, error: configError };
      }
      
      const formattedError = this.formatErrorMessage(error);
      return { success: false, error: formattedError };
    }
  }

  // Sign in with Azure IDP (OAuth/SAML)
  async signInWithAzure() {
    try {
      
      // Check if OAuth is properly configured
      if (!isOAuthEnabled()) {
        const error = 'Azure sign-in is not configured. Please check your OAuth domain settings.';
        return { success: false, error };
      }

      // Check if authentication is properly configured
      if (!validateAuthConfig()) {
        return { success: false, error: 'Authentication is not configured. Please check your environment variables.' };
      }

      // Use SAML-specific client ID for Azure sign-in
      const samlClientId = process.env.REACT_APP_COGNITO_SAML_CLIENT_ID;
      if (!samlClientId) {
        const error = 'SAML Client ID is not configured. Please check REACT_APP_COGNITO_SAML_CLIENT_ID environment variable.';
        return { success: false, error };
      }

      // Debug OAuth configuration before redirect
      const currentOrigin = `${window.location.protocol}//${window.location.host}`;
      const redirectUrl = process.env.REACT_APP_OAUTH_REDIRECT_SIGN_IN || currentOrigin;
      
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('Azure SAML OAuth redirect initiated');
      }

      // Build the OAuth URL using SAML client ID
      const oauthUrl = `https://${process.env.REACT_APP_COGNITO_OAUTH_DOMAIN}/oauth2/authorize?client_id=${samlClientId}&response_type=code&scope=openid&redirect_uri=${encodeURIComponent(redirectUrl)}&identity_provider=LabbAzure`;
      
      // Mark that SAML authentication is being initiated
      sessionStorage.setItem('saml_auth_initiated', 'true');
      
      // Flag to suppress the beforeunload "are you sure?" prompt
      sessionStorage.setItem('intentional_redirect', 'true');
      
      // Use direct window.location redirect
      window.location.href = oauthUrl;
      
      return { success: true, redirecting: true };
      
    } catch (error) {
      const formattedError = this.formatErrorMessage(error);
      return { success: false, error: formattedError };
    }
  }

  // Check if user is returning from OAuth redirect
  async handleOAuthCallback(retryCode = null) {
    try {
      
      // Check for authorization code in URL or use retry code
      const urlParams = new URLSearchParams(window.location.search);
      let authCode = retryCode || urlParams.get('code');
      const error = urlParams.get('error');
      const state = urlParams.get('state');
      
      // Store auth code for potential retry if this is the first attempt
      if (!retryCode && authCode) {
        sessionStorage.setItem('oauth_retry_code', authCode);
        sessionStorage.setItem('oauth_retry_state', state || '');
      }
      
      // If no code in URL, check for stored retry code
      if (!authCode) {
        authCode = sessionStorage.getItem('oauth_retry_code');
      }
      
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('OAuth callback analysis:', {
          hasCode: !!authCode,
          hasError: !!error,
          hasState: !!state,
          isRetry: !!retryCode
        });
      }
      
      if (error) {
        console.error('OAuth callback error:', error);
        this.clearOAuthRetryData();
        return { success: false, error: `OAuth error: ${error}` };
      }
      
      if (!authCode) {
        return { success: false, error: 'No authorization code found' };
      }
      
      // Get SAML client ID for this OAuth flow
      const samlClientId = process.env.REACT_APP_COGNITO_SAML_CLIENT_ID;
      
      // Check if this is a SAML callback (from our Azure login)
      const storedRetryState = sessionStorage.getItem('oauth_retry_state');
      const effectiveState = state || storedRetryState || '';
      
      const isSAMLCallback = effectiveState.includes('saml') || 
                            window.location.href.includes('identity_provider=LabbAzure') ||
                            document.referrer.includes('microsoftonline.com') ||
                            document.referrer.includes('login.microsoftonline.com') ||
                            sessionStorage.getItem('saml_auth_initiated') === 'true';
      
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('OAuth callback analysis:', {
          isSAMLCallback,
          referrer: document.referrer
        });
      }
      
      if (isSAMLCallback && samlClientId) {
        try {
          // Exchange authorization code for tokens manually
          const tokenResponse = await this.exchangeCodeForTokens(authCode, samlClientId);
          
          if (tokenResponse.success) {
            
            // Clear OAuth retry data
            this.clearOAuthRetryData();
            
            // Clear URL parameters
            const cleanUrl = window.location.protocol + '//' + window.location.host + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
            
            return { success: true, user: tokenResponse.user };
          } else {
            return { success: false, error: tokenResponse.error };
          }
          
        } catch (exchangeError) {
          console.error('❌ Manual token exchange failed:', exchangeError);
          return { success: false, error: `Token exchange failed: ${exchangeError.message}` };
        }
      }
      
      // For regular OAuth (non-SAML), try the standard Amplify flow
      // Wait a moment for Amplify to process the callback
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      try {
        const user = await getCurrentUser();
        
        // Clear OAuth retry data
        this.clearOAuthRetryData();
        
        // Clear URL parameters
        const cleanUrl = window.location.protocol + '//' + window.location.host + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        
        return { success: true, user };
      } catch (userError) {
        console.warn('⚠️ Auth code present but user not authenticated yet:', userError.message);
        
        return { 
          success: false, 
          error: 'Authentication in progress, please wait...', 
          retry: true,
          code: authCode
        };
      }
      
    } catch (error) {
      console.error('❌ OAuth callback handling failed:', error);
      return { success: false, error: this.formatErrorMessage(error) };
    }
  }

  // Exchange authorization code for tokens (for SAML OAuth flow)
  async exchangeCodeForTokens(authCode, clientId) {
    try {
      
      const domain = process.env.REACT_APP_COGNITO_OAUTH_DOMAIN;
      const redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_SIGN_IN || window.location.origin;
      
      const tokenEndpoint = `https://${domain}/oauth2/token`;
      
      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code: authCode,
        redirect_uri: redirectUri
      });
      
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('Token exchange initiated');
      }
      
      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString()
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Token exchange failed:', response.status, errorData);
        throw new Error(`Token exchange failed: ${response.status} ${errorData}`);
      }
      
      const tokens = await response.json();        // Store tokens in Amplify's storage/session
        if (tokens.access_token && tokens.id_token) {
          // Decode the ID token to get user info
          const idTokenPayload = this.decodeJWT(tokens.id_token);
          
          // Store tokens in Amplify-compatible format
          await this.storeAmplifyTokens(tokens, idTokenPayload);
          
          // Create user object similar to Amplify's format
          const user = {
            username: idTokenPayload.sub || idTokenPayload.email,
            attributes: {
              email: idTokenPayload.email,
              email_verified: idTokenPayload.email_verified,
              given_name: idTokenPayload.given_name,
              family_name: idTokenPayload.family_name,
              name: idTokenPayload.name
            }
          };
          
          return { success: true, user, tokens };
        }
      
      throw new Error('Invalid tokens received');
      
    } catch (error) {
      console.error('❌ exchangeCodeForTokens failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Decode JWT token (simple base64 decode)
  decodeJWT(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return {};
    }
  }

  // Authenticate using a pre-obtained Cognito ID token (native app guest flow).
  // Stores the token via the same sessionStorage path used by the SAML flow so
  // that getToken() and checkSAMLAuthentication() can find it on subsequent calls.
  async signInWithToken(idToken) {
    const payload = this.decodeJWT(idToken);
    if (!payload || !payload.sub) {
      throw new Error('Invalid or expired token');
    }

    this.token = idToken;
    localStorage.setItem(API_CONFIG.jwt.storageKey, idToken);
    sessionStorage.setItem('cognito_tokens', JSON.stringify({ id_token: idToken }));
    sessionStorage.setItem('cognito_user_data', JSON.stringify(payload));

    return {
      username: payload.sub || payload.email,
      attributes: {
        sub: payload.sub,
        email: payload.email,
        email_verified: payload.email_verified,
        given_name: payload.given_name,
        family_name: payload.family_name,
        name: payload.name,
      },
    };
  }

  // Sign out
  async signOut() {
    try {
      // Clear SAML tokens first
      this.clearSAMLTokens();
      
      // Clear Amplify session
      await signOut();
      
      // Clear local state
      this.currentUser = null;
      this.token = null;
      localStorage.removeItem(API_CONFIG.jwt.storageKey);
      
      return { success: true };
    } catch (error) {
      console.error('Sign out failed:', error);
      
      // Even if Amplify sign out fails, clear our local tokens
      this.clearSAMLTokens();
      this.currentUser = null;
      this.token = null;
      localStorage.removeItem(API_CONFIG.jwt.storageKey);
      
      return { success: false, error: this.formatErrorMessage(error) };
    }
  }

  // Sign up new user
  async signUp(username, password, email, attributes = {}) {
    try {
      // Check if authentication is properly configured
      if (!validateAuthConfig()) {
        return { success: false, error: 'Authentication is not configured. Please check your environment variables.' };
      }

      // Merge email with any additional attributes
      const userAttributes = {
        email,
        ...attributes
      };

      const result = await signUp({
        username,
        password,
        options: {
          userAttributes
        }
      });
      
      // Check if user needs confirmation - based on actual AWS response
      const requiresConfirmation = 
        result.UserConfirmed === false ||           // Direct AWS response
        result.userConfirmed === false ||           // Alternative format
        !result.isSignUpComplete ||                 // Amplify v6 format
        !!result.CodeDeliveryDetails;               // If code was sent, confirmation needed
      
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('SignUp result:', {
          isSignUpComplete: result.isSignUpComplete,
          requiresConfirmation
        });
      }
      
      return { 
        success: true, 
        user: result.user, 
        userSub: result.userId,
        requiresConfirmation: requiresConfirmation
      };
    } catch (error) {
      console.error('Sign up failed:', error);
      return { success: false, error: this.formatErrorMessage(error) };
    }
  }

  // Confirm sign up
  async confirmSignUp(username, code) {
    try {
      await confirmSignUp({ username, confirmationCode: code });
      return { success: true };
    } catch (error) {
      console.error('Confirm sign up failed:', error);
      return { success: false, error: this.formatErrorMessage(error) };
    }
  }

  // Resend confirmation code
  async resendConfirmationCode(username) {
    try {
      await resendSignUpCode({ username });
      return { success: true };
    } catch (error) {
      console.error('Resend confirmation code failed:', error);
      return { success: false, error: this.formatErrorMessage(error) };
    }
  }

  // Forgot password
  async forgotPassword(username) {
    try {
      console.log('🔐 Initiating password reset for:', username);
      
      const result = await resetPassword({ username });
      
      console.log('✅ Password reset initiated successfully');
      console.log('📧 Verification code should be sent to:', username);
      
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('Password reset result:', result);
      }
      
      return { success: true };
    } catch (error) {
      console.error('❌ Forgot password failed:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        code: error.code,
        __type: error.__type
      });
      
      // Check for specific error indicating no verified email
      if (error.message && error.message.includes('no registered/verified email')) {
        console.error('⚠️  No verified email found for user. This usually means:');
        console.error('   1. User email is not verified in Cognito');
        console.error('   2. User does not exist');
        console.error('   3. Cognito email configuration issue');
      }
      
      return { success: false, error: this.formatErrorMessage(error) };
    }
  }

  // Forgot password submit (confirm reset)
  async forgotPasswordSubmit(username, code, newPassword) {
    try {
      console.log('🔐 Confirming password reset for:', username);
      
      await confirmResetPassword({ 
        username, 
        confirmationCode: code, 
        newPassword 
      });
      
      console.log('✅ Password reset confirmed successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Forgot password submit failed:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        code: error.code,
        __type: error.__type
      });
      
      // Check for specific errors
      if (error.message && error.message.includes('Invalid verification code')) {
        console.error('⚠️  Invalid or expired verification code');
      } else if (error.message && error.message.includes('Password does not conform')) {
        console.error('⚠️  Password does not meet requirements');
      }
      
      return { success: false, error: this.formatErrorMessage(error) };
    }
  }

  // Get user attributes
  async getUserAttributes() {
    try {
      // First check if user is authenticated via SAML (manual token storage)
      const samlAuth = await this.checkSAMLAuthentication();
      if (samlAuth.success && samlAuth.user?.attributes) {
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('✅ Returning user attributes from SAML authentication');
        }
        return samlAuth.user.attributes;
      }

      // Fall back to Amplify's fetchUserAttributes for standard Cognito users
      const attributes = await fetchUserAttributes();
      return attributes;
    } catch (error) {
      console.error('Get user attributes failed:', error);
      return null;
    }
  }

  // Decode sub from the stored JWT without a network call.
  // Works for both standard Cognito tokens and SAML tokens — both carry sub in the payload.
  getSub() {
    try {
      if (!this.token) return null;
      return JSON.parse(atob(this.token.split('.')[1])).sub || null;
    } catch {
      return null;
    }
  }

  // Event handlers
  async handleSignIn(user) {
    console.log('User signed in:', user);
    
    // Immediately try to get and store the JWT token after sign in
    try {
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('🔄 Extracting JWT token after sign in...');
      }
      
      // Get fresh session after sign in
      const session = await fetchAuthSession();
      if (session?.tokens?.idToken) {
        this.token = session.tokens.idToken.toString();
        localStorage.setItem(API_CONFIG.jwt.storageKey, this.token);
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('✅ JWT token stored in localStorage after sign in');
        }
      } else {
        console.warn('⚠️ No JWT token available immediately after sign in');
      }
    } catch (error) {
      console.error('❌ Error storing JWT token after sign in:', error);
    }
    
    // Then get current user as usual
    this.getCurrentUser();
  }

  handleSignOut() {
    console.log('User signed out');
    this.currentUser = null;
    this.token = null;
    localStorage.removeItem(API_CONFIG.jwt.storageKey);
  }

  handleSignInFailure(error) {
    console.error('Sign in failure event:', error);
  }

  handleTokenRefresh(session) {
    console.log('Token refreshed');
    if (session?.tokens?.idToken) {
      this.token = session.tokens.idToken.toString();
      localStorage.setItem(API_CONFIG.jwt.storageKey, this.token);
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('✅ JWT token updated after refresh');
      }
    } else {
      console.warn('⚠️ No ID token in refresh event');
    }
  }

  handleTokenRefreshFailure(error) {
    console.error('Token refresh failure:', error);
  }

  // Check if user is authenticated via SAML (manual token storage)
  async checkSAMLAuthentication() {
    try {
      // First check if we have tokens in sessionStorage from SAML flow
      const storedTokens = sessionStorage.getItem('cognito_tokens');
      const storedUser = sessionStorage.getItem('cognito_user_data');
      
      if (storedTokens && storedUser) {
        const tokens = JSON.parse(storedTokens);
        const userData = JSON.parse(storedUser);
        
        // Check if tokens are still valid
        const now = Math.floor(Date.now() / 1000);
        if (userData.exp && userData.exp > now) {

          
          // Create user object
          const user = {
            username: userData.sub || userData.email,
            attributes: {
              email: userData.email,
              email_verified: userData.email_verified,
              given_name: userData.given_name,
              family_name: userData.family_name,
              name: userData.name
            }
          };
          
          return { success: true, user, tokens };
        } else {

          this.clearSAMLTokens();
        }
      }
      
      return { success: false };
    } catch (error) {
      console.error('❌ Error checking SAML authentication:', error);
      return { success: false };
    }
  }

  // Clear SAML tokens from storage
  clearSAMLTokens() {
    try {
      sessionStorage.removeItem('cognito_tokens');
      sessionStorage.removeItem('cognito_user_data');
      
      // Also clear from localStorage if present
      const clientId = process.env.REACT_APP_COGNITO_SAML_CLIENT_ID;
      if (clientId) {
        const lastAuthUserKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`;
        const lastUser = localStorage.getItem(lastAuthUserKey);
        
        if (lastUser) {
          const userSessionKey = `CognitoIdentityServiceProvider.${clientId}.${lastUser}`;
          localStorage.removeItem(`${userSessionKey}.accessToken`);
          localStorage.removeItem(`${userSessionKey}.idToken`);
          localStorage.removeItem(`${userSessionKey}.refreshToken`);
          localStorage.removeItem(`${userSessionKey}.tokenScopesString`);
          localStorage.removeItem(`${userSessionKey}.userData`);
          localStorage.removeItem(lastAuthUserKey);
        }
      }
      

    } catch (error) {
      console.error('❌ Error clearing SAML tokens:', error);
    }
  }

  // Clear OAuth retry data
  clearOAuthRetryData() {
    try {
      sessionStorage.removeItem('oauth_retry_code');
      sessionStorage.removeItem('oauth_retry_state');
      sessionStorage.removeItem('saml_auth_initiated');

    } catch (error) {
      console.error('❌ Error clearing OAuth retry data:', error);
    }
  }

  // Get JWT token for API calls
  async getToken() {
    if (process.env.REACT_APP_SKIP_AUTH === 'true') {
      return null; // Auth disabled
    }

    // Check if we already have a token in memory or localStorage
    const existingToken = this.token || localStorage.getItem(API_CONFIG.jwt.storageKey);
    if (existingToken) {
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('🔍 Using existing JWT token');
      }
      return existingToken;
    }

    // If no token available, try to get fresh session (critical after SAML SSO)
    try {
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('🔄 No existing token, fetching fresh session...');
      }

      const session = await fetchAuthSession({ forceRefresh: true });
      if (session?.tokens?.idToken) {
        this.token = session.tokens.idToken.toString();
        localStorage.setItem(API_CONFIG.jwt.storageKey, this.token);
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('✅ Retrieved and stored fresh JWT token from session');
        }
        return this.token;
      } else {
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('🚫 No ID token in fresh session');
        }
      }
    } catch (error) {
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('🚫 Could not retrieve fresh auth token:', error.message);
      }
    }

    // As a last resort, check if user is authenticated via SAML manually
    try {
      const samlAuth = await this.checkSAMLAuthentication();
      if (samlAuth.success && samlAuth.tokens?.id_token) {
        this.token = samlAuth.tokens.id_token;
        localStorage.setItem(API_CONFIG.jwt.storageKey, this.token);
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('✅ Retrieved JWT token from SAML authentication');
        }
        return this.token;
      }
    } catch (error) {
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('🚫 SAML authentication check failed:', error.message);
      }
    }

    if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
      console.log('🚫 No JWT token available from any source');
    }
    return null;
  }

  // Get authorization header for API calls
  getAuthHeader() {
    if (this.token) {
      return {
        [API_CONFIG.jwt.headerName]: `${API_CONFIG.jwt.tokenPrefix}${this.token}`
      };
    }
    return {};
  }

  // Store tokens in Amplify-compatible format
  async storeAmplifyTokens(tokens, idTokenPayload) {
    try {

      
      // Get required values for token storage
      const clientId = process.env.REACT_APP_COGNITO_SAML_CLIENT_ID;
      const username = idTokenPayload.sub || idTokenPayload.email;
      
      // Store user session data
      const userSessionKey = `CognitoIdentityServiceProvider.${clientId}.${username}`;
      const lastAuthUserKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`;
      
      // Store tokens with Amplify's naming convention
      localStorage.setItem(`${userSessionKey}.accessToken`, tokens.access_token);
      localStorage.setItem(`${userSessionKey}.idToken`, tokens.id_token);
      if (tokens.refresh_token) {
        localStorage.setItem(`${userSessionKey}.refreshToken`, tokens.refresh_token);
      }
      localStorage.setItem(`${userSessionKey}.tokenScopesString`, 'openid profile email');
      localStorage.setItem(`${userSessionKey}.userData`, JSON.stringify(idTokenPayload));
      localStorage.setItem(lastAuthUserKey, username);
      
      // Also store in sessionStorage for backup
      sessionStorage.setItem('cognito_tokens', JSON.stringify(tokens));
      sessionStorage.setItem('cognito_user_data', JSON.stringify(idTokenPayload));
      
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('Tokens stored in Amplify format');
      }
      return true;
      
    } catch (error) {
      console.error('❌ Failed to store Amplify tokens:', error);
      throw error;
    }
  }
}

// Initialize authentication
initializeAuth();

// Export singleton instance
export const authService = new AuthService();
export default authService;
